import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth, roleHome } from '@/context/AuthContext'
import { errorMessage } from '@/lib/api'
import Button from '@/components/ui/Button'
import { useToast } from '@/components/ui/toast'
import AuthShell from './AuthShell'

/**
 * Where Google sign-in lands. The code in the query string is good for one
 * exchange and two minutes, so this runs immediately and never twice.
 */
export default function OAuthCallback() {
  const { completeOAuth } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // React runs effects twice in development StrictMode. The code is
  // single-use, so without this guard the second run always fails.
  const exchanged = useRef(false)

  useEffect(() => {
    const code = params.get('code')

    if (!code) {
      setError('This sign-in link is incomplete. Please try again.')
      return
    }

    if (exchanged.current) return
    exchanged.current = true

    completeOAuth(code)
      .then((user) => {
        toast.success('Signed in with Google')
        navigate(roleHome(user), { replace: true })
      })
      .catch((err) => setError(errorMessage(err)))
  }, [completeOAuth, navigate, params, toast])

  if (error) {
    return (
      <AuthShell
        heading="Sign in failed"
        subheading="That Google sign-in could not be completed"
        tagline={<>Let us try that again.</>}
      >
        <div className="mt-7 space-y-4">
          <div className="rounded-lg border border-line bg-foreground/5 p-4 text-sm text-foreground/80">{error}</div>
          <Button className="w-full" onClick={() => navigate('/login', { replace: true })}>
            Back to sign in
          </Button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      heading="Signing you in"
      subheading="One moment while we finish with Google"
      tagline={<>Almost there.</>}
    >
      <div className="mt-10 flex justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    </AuthShell>
  )
}
